import { motion, AnimatePresence } from "framer-motion";
import { X, Shovel, Ruler, Mountain, Layers, ArrowDownUp, Droplets, Compass, Blocks, ChevronDown, Phone } from "lucide-react";
import { useState } from "react";

const services = [
  { icon: Shovel, title: "Limpieza y Desmonte", desc: "Eliminación de vegetación, residuos y materiales que impiden la preparación del terreno." },
  { icon: Compass, title: "Trazo y Nivelación", desc: "Marcaje del proyecto en campo con base en planos arquitectónicos y nivelación inicial del terreno." },
  { icon: Mountain, title: "Excavación y Relleno", desc: "Movimiento de tierras para alcanzar los niveles requeridos en el diseño del proyecto." },
  { icon: Layers, title: "Compactación del Suelo", desc: "Consolidación del terreno para mejorar su capacidad de carga y estabilidad estructural." },
  { icon: ArrowDownUp, title: "Cortes y Rellenos", desc: "Ajuste del terreno para generar pendientes, plataformas y niveles adecuados para la obra." },
  { icon: Droplets, title: "Sistemas de Drenaje", desc: "Instalación de drenaje pluvial que evita acumulación de agua y protege la estabilidad del suelo." },
  { icon: Ruler, title: "Preparación de Subrasante", desc: "Formación de la capa estructural del terreno que soportará la obra o pavimento." },
  { icon: Blocks, title: "Base y Subbase", desc: "Colocación de materiales estructurales que proporcionan estabilidad y distribución de cargas." },
];

const equipment = ["Excavadoras", "Bulldozers", "Compactadoras", "Motoniveladoras", "Camiones de carga", "Equipos de topografía de precisión"];
const benefits = ["Mayor estabilidad estructural", "Reducción de asentamientos del terreno", "Mejor drenaje y control de humedad", "Mayor vida útil de la obra"];

export default function TerraceriaModal({ open, onClose }) {
  const [expandedItem, setExpandedItem] = useState(null);

  const toggle = (i) => setExpandedItem(expandedItem === i ? null : i);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto"
          data-testid="terraceria-modal"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/85 backdrop-blur-sm" />

          {/* Content panel */}
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-4xl mx-4 my-12 bg-[#0A0A0A] rounded-3xl border border-white/[0.06] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              data-testid="terraceria-close"
              className="absolute top-5 right-5 z-10 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
            >
              <X size={18} />
            </button>

            {/* Hero header */}
            <div className="px-8 md:px-14 pt-12 pb-10 border-b border-white/[0.06]">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-500 mb-4"
              >
                Servicio Especializado
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-2xl md:text-4xl font-normal text-white tracking-tight mb-5"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {"Terracería Profesional para Proyectos de Construcción"}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-sm md:text-base text-neutral-400 leading-relaxed max-w-2xl"
              >
                {"Preparamos tu terreno para construir con precisión técnica, maquinaria especializada y base en estudios de mecánica de suelos."}
              </motion.p>
            </div>

            {/* Description paragraphs */}
            <div className="px-8 md:px-14 py-8 border-b border-white/[0.06]">
              <p className="text-sm text-neutral-400 leading-relaxed mb-4">
                {"Nuestro servicio de terracería garantiza que el terreno quede estable, nivelado y estructuralmente preparado para recibir la cimentación, reduciendo riesgos de asentamientos, filtraciones o fallas estructurales en el futuro."}
              </p>
              <p className="text-sm text-neutral-500 leading-relaxed">
                {"Realizamos todos los trabajos preliminares necesarios para el inicio seguro de tu obra, siguiendo criterios técnicos y estándares de ingeniería."}
              </p>
            </div>

            {/* Services accordion */}
            <div className="px-8 md:px-14 py-6">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-6">
                {"Qué incluye nuestro servicio"}
              </p>
              <div className="space-y-0">
                {services.map((s, i) => (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.04 }}
                    className="border-b border-white/[0.05] last:border-0"
                  >
                    <button
                      onClick={() => toggle(i)}
                      data-testid={`terraceria-item-${i}`}
                      className="w-full flex items-center gap-4 py-4 text-left group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0 group-hover:bg-white/[0.08] transition-colors">
                        <s.icon size={16} className="text-neutral-400" />
                      </div>
                      <span className="flex-1 text-sm md:text-base text-white/90 font-medium tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {s.title}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`text-neutral-500 transition-transform duration-300 ${expandedItem === i ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {expandedItem === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <p className="pb-4 text-sm text-neutral-500 leading-relaxed ml-[52px]">
                            {s.desc}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Equipment + Benefits — two-column grid */}
            <div className="px-8 md:px-14 py-8 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Equipment */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-5">
                  {"Maquinaria y Tecnología"}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {equipment.map((e) => (
                    <div key={e} className="flex items-center gap-2 py-2 px-3 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                      <span className="w-1.5 h-1.5 rounded-full bg-neutral-600" />
                      <span className="text-xs text-neutral-400">{e}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-5">
                  {"Por qué es importante"}
                </p>
                <div className="space-y-3">
                  {benefits.map((b) => (
                    <div key={b} className="flex items-start gap-3">
                      <div className="w-5 h-5 mt-0.5 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
                      </div>
                      <span className="text-sm text-neutral-400">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="px-8 md:px-14 py-8 border-t border-white/[0.06] bg-white/[0.02]">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <p className="text-base text-white font-medium mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {"Solicita una Evaluación de tu Terreno"}
                  </p>
                  <p className="text-sm text-neutral-500">
                    {"Nuestro equipo puede analizar tu proyecto y determinar el proceso más adecuado."}
                  </p>
                </div>
                <button
                  onClick={() => { onClose(); setTimeout(() => document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" }), 300); }}
                  data-testid="terraceria-cta"
                  className="flex items-center gap-2 px-6 py-3 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors flex-shrink-0"
                >
                  <Phone size={14} />
                  Contactar
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
